// useAutosave — first-class autosave hook for Inertia + Rails forms.
//
// Each bound field owns local state. Caller seeds the initial value via
// the `initial` option on `bind()`. On blur (or after `debounceMs` of
// idle in "debounce" mode) the hook fires a PATCH to `url` with the
// shape `{ agent: { [field]: value } }` — matching Rails' strong-params
// idiom.
//
// Usage:
//   const autosave = useAutosave({ url: `/agents/${agent.id}`,
//     onSaved: (a) => setAgent(a) })
//   const name = autosave.bind("name", { initial: agent.name })
//   <input {...name} />
//   <span>{autosave.status}</span>
//
// We use plain `fetch` (not Inertia's router) because we need the JSON
// agent back from the server to refresh "lastUpdatedAt" without
// re-rendering the whole page.

import { useCallback, useEffect, useRef, useState } from "react"

export type AutosaveStatus = "idle" | "saving" | "saved" | "error"

type BindMode = "blur" | "debounce"

type BindOpts<T> = {
  mode?: BindMode
  initial: T
}

type StringBinding = {
  value: string
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void
  onBlur: () => void
}

type Options<TAgent> = {
  url: string
  debounceMs?: number
  onSaved?: (agent: TAgent, field: string) => void
  onError?: (message: string, field: string) => void
}

type AutosaveApi = {
  bind: (field: string, opts: BindOpts<string>) => StringBinding
  /** Imperatively patch a single field. Used for non-text inputs (toggles,
   *  dropdowns, JSON editors that parse on blur). */
  patch: (field: string, value: unknown) => Promise<void>
  status: AutosaveStatus
  lastSavedAt: string | null
}

function csrfToken(): string {
  const el = document.querySelector<HTMLMetaElement>("meta[name=csrf-token]")
  return el?.content ?? ""
}

export function useAutosave<TAgent = unknown>({
  url,
  debounceMs = 600,
  onSaved,
  onError,
}: Options<TAgent>): AutosaveApi {
  const [status, setStatus] = useState<AutosaveStatus>("idle")
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null)
  // Bumped on every local edit; used purely to force re-render. The
  // actual per-field values live in `valuesRef` so we don't need to
  // worry about stale closures inside debounce timers.
  const [, setTick] = useState(0)
  const bump = useCallback(() => setTick((t) => t + 1), [])

  const valuesRef = useRef<Record<string, string>>({})
  const lastSavedRef = useRef<Record<string, string>>({})
  const debounceTimers = useRef<Record<string, number | null>>({})
  const savedFlipTimer = useRef<number | null>(null)

  useEffect(() => {
    return () => {
      Object.values(debounceTimers.current).forEach((t) => {
        if (t !== null) window.clearTimeout(t)
      })
      if (savedFlipTimer.current !== null) window.clearTimeout(savedFlipTimer.current)
    }
  }, [])

  const flipToSaved = useCallback(() => {
    setStatus("saved")
    setLastSavedAt(new Date().toISOString())
    if (savedFlipTimer.current !== null) window.clearTimeout(savedFlipTimer.current)
    savedFlipTimer.current = window.setTimeout(() => {
      setStatus("idle")
      savedFlipTimer.current = null
    }, 1500)
  }, [])

  const sendPatch = useCallback(
    async (field: string, rawValue: unknown) => {
      setStatus("saving")
      try {
        const res = await fetch(url, {
          method: "PATCH",
          credentials: "same-origin",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            "X-CSRF-Token": csrfToken(),
            "X-Requested-With": "XMLHttpRequest",
          },
          body: JSON.stringify({ agent: { [field]: rawValue } }),
        })
        if (!res.ok) {
          const msg = `PATCH ${url} failed: HTTP ${res.status}`
          setStatus("error")
          onError?.(msg, field)
          return
        }
        // Best-effort JSON parse — endpoint may return `{ agent: {...} }`
        // or the agent payload directly.
        let body: unknown = null
        try {
          body = await res.json()
        } catch {
          // ignore; still treat as saved.
        }
        const agent =
          body && typeof body === "object" && body !== null && "agent" in body
            ? (body as { agent: TAgent }).agent
            : (body as TAgent | null)
        if (agent) onSaved?.(agent, field)
        flipToSaved()
      } catch (e) {
        setStatus("error")
        onError?.((e as Error).message, field)
      }
    },
    [url, onSaved, onError, flipToSaved],
  )

  const patch = useCallback(
    async (field: string, value: unknown) => {
      if (typeof value === "string") {
        valuesRef.current[field] = value
        lastSavedRef.current[field] = value
        bump()
      }
      await sendPatch(field, value)
    },
    [sendPatch, bump],
  )

  const bind = useCallback(
    (field: string, opts: BindOpts<string>): StringBinding => {
      // Seed initial value once per field. Subsequent re-renders read
      // from the ref so parent prop drift can't clobber local edits.
      if (!(field in valuesRef.current)) {
        const init = opts.initial ?? ""
        valuesRef.current[field] = init
        lastSavedRef.current[field] = init
      }

      const mode: BindMode = opts.mode ?? "blur"
      const value = valuesRef.current[field] ?? ""

      const onChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
      ) => {
        const next = e.target.value
        valuesRef.current[field] = next
        bump()
        if (mode !== "debounce") return
        const existing = debounceTimers.current[field]
        if (existing !== null && existing !== undefined) window.clearTimeout(existing)
        debounceTimers.current[field] = window.setTimeout(() => {
          debounceTimers.current[field] = null
          if (lastSavedRef.current[field] === next) return
          lastSavedRef.current[field] = next
          void sendPatch(field, next)
        }, debounceMs)
      }

      const onBlur = () => {
        const cur = valuesRef.current[field] ?? ""
        // Cancel any pending debounce — blur takes precedence.
        const existing = debounceTimers.current[field]
        if (existing !== null && existing !== undefined) {
          window.clearTimeout(existing)
          debounceTimers.current[field] = null
        }
        if (lastSavedRef.current[field] === cur) return
        lastSavedRef.current[field] = cur
        void sendPatch(field, cur)
      }

      return { value, onChange, onBlur }
    },
    [debounceMs, sendPatch, bump],
  )

  return { bind, patch, status, lastSavedAt }
}
